import { AppContext } from "src/app/ui/contexts/app-context";
import { UserRole } from "./../models/domains/pbm/user/user-role";
import { PageableResult } from "src/app/ui/models/domains/paginator/pageable-result";
import { Injectable } from "@angular/core";

import { EmailAddress } from "../models/domains/email/email-address";
import { UserRepository } from "../repositories/user.repository";
import { ResetPasswordRepository } from "../repositories/reset-password.repository";
import { UserCredentials } from "../models/domains/credentials/user-credentials.model";
import { User } from "../models/domains/pbm/user/user";
import { PagingRequest } from "../models/domains/paginator/paging-request";
import { UserProfile } from "../models/domains/pbm/user/user-profile";
import { UserSearchPreferences } from "../models/domains/pbm/user/user-search-preferences";

@Injectable({ providedIn: "root" })
export class UserService {
  constructor(private userRepository: UserRepository, private resetPasswordRepository: ResetPasswordRepository, private appContext: AppContext) {}

  public async validateUserActivationToken(token: string): Promise<UserCredentials> {
    try {
      const activeUser = this.getUserCredentialsFirstAccess(token);
      const validToken = Promise.resolve(await this.userRepository.activateUser(activeUser));
      if (validToken) {
        return validToken;
      } else {
        return null;
      }
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public getUserCredentialsFirstAccess(token: string): UserCredentials {
    try {
      if (token.length % 4 > 0) {
        return null;
      }
      let params = atob(token);
      params = JSON.parse(params);
      const userId = params["UserId"];
      const userCredentials = new UserCredentials();
      userCredentials.setActionNameConfirmation(params["ActionNameConfirmation"]);
      userCredentials.setToken(token);
      userCredentials.setUserId(userId);
      return userCredentials;
    } catch (error) {
      return null;
    }
  }

  public async changePasswordFirstAccess(password: string, token: string, userId: string): Promise<boolean> {
    try {
      const userCredentials = new UserCredentials();
      userCredentials.userId = userId;
      userCredentials.token = token;
      userCredentials.password = password;
      return Promise.resolve(await this.userRepository.changePasswordFirstAccess(password, userCredentials));
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async sendEmailForgotPassword(email: EmailAddress): Promise<any> {
    try {
      return Promise.resolve(await this.userRepository.sendEmailForgotPassword(email));
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async resetPassword(password: string, token: string): Promise<boolean> {
    try {
      const userCredentials = this.isTokenValid(token);
      return Promise.resolve(await this.resetPasswordRepository.resetPassword(password, userCredentials));
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public async changePassword(currentPassword: string, newPassword: string, confirmPassword: string): Promise<void> {
    try {
      await this.userRepository.changePassword(currentPassword, newPassword, confirmPassword);
      return Promise.resolve();
    } catch (error) {
      return await Promise.reject(error);
    }
  }

  public isTokenValid(token: string): UserCredentials {
    try {
      if (token.length % 4 > 0) {
        return null;
      }
      let params = atob(token);
      params = JSON.parse(params);
      const userId = params["UserId"];
      if (!userId || !this.isGuidValid(userId)) {
        throw new Error("Invalid user id");
      }
      const resetToken = params["Token"];
      if (!resetToken) {
        throw new Error("Invalid reset token");
      }
      const userCredentials = new UserCredentials();
      userCredentials.setUserId(userId);
      userCredentials.setToken(resetToken);
      return userCredentials;
    } catch (error) {
      return null;
    }
  }

  private isGuidValid(guid: string): boolean {
    const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return guidRegex.test(guid);
  }

  public async searchUser(pagingRequest: PagingRequest): Promise<PageableResult> {
    let userFiscalNumber = "";
    if (!this.getLoggedUser().isAdministrator()) {
      userFiscalNumber = this.getLoggedUser().getUserCompanyInfo().getFiscalNumber();
    }
    return await this.userRepository.searchUser(pagingRequest, userFiscalNumber);
  }

  public async requestUserRoles(): Promise<UserRole[]> {
    return await this.userRepository.requestUserRoles();
  }

  public async addUser(user: User, password: string): Promise<string> {
    return await this.userRepository.addUser(user, password);
  }

  public async updateUser(user: User, newPassword: string, confirmNewPassword: string): Promise<string> {
    return await this.userRepository.updateUser(user, newPassword, confirmNewPassword);
  }

  public async searchUserByUserKey(userKey: string): Promise<User> {
    let userFiscalNumber = "";
    if (!this.getLoggedUser().isAdministrator()) {
      userFiscalNumber = this.getLoggedUser().getUserCompanyInfo().getFiscalNumber();
    }
    return await this.userRepository.searchUserByUserKey(userKey, userFiscalNumber);
  }

  public getLoggedUser(): User {
    return this.appContext.getUser();
  }

  public getSearchPreferences(): UserSearchPreferences {
    return this.appContext.getUserSearchPreferences();
  }

  public setUserPreferences(filterText: string): void {
    const userSearchPreferences = this.getSearchPreferences();
    userSearchPreferences.setFilterText(filterText);
    this.appContext.setUserSearchPreferences(userSearchPreferences);
  }

  public setTransactionalManagementAutoPageRefresh(value: boolean): void {
    const userSearchPreferences = this.getSearchPreferences();
    userSearchPreferences.setTransactionalManagementAutoPageRefresh(value);
    this.appContext.setUserSearchPreferences(userSearchPreferences);
  }

  public getUserProfile(): UserProfile {
    return new UserProfile(this.getLoggedUser(), this.getSearchPreferences());
  }
}
